import { useFormContext } from 'react-hook-form';
import { Box, css } from '@mui/material';

import { Text } from '~/components/ui/text';
import { VideoQRSchema } from '~/pages/CreateQR/sub-components/videoTemplate/schema';
import { COLORS } from '~/styles/colors';
import { mainPreviewContainerStyles } from '~/styles/common';
import { Video, VideoQrResponse } from '~/types/video';
import { convertYouTubeToEmbed } from '~/utils/video';

type VideoPreviewProps = {
  isMyMicrosite?: boolean;
  videoQrData?: VideoQrResponse;
};

export default function VideoPreview({ isMyMicrosite, videoQrData }: VideoPreviewProps) {
  const context = useFormContext<VideoQRSchema>();
  const styles = useStyles();
  const { watch } = context || {};

  const company = isMyMicrosite ? videoQrData?.company : watch('basicInfo.information.company');
  const videoTitle = isMyMicrosite ? videoQrData?.title : watch('basicInfo.information.videoTitle');
  const description = isMyMicrosite ? videoQrData?.description : watch('basicInfo.information.description');
  const buttonText = isMyMicrosite ? videoQrData?.ctaText : watch('basicInfo.information.buttonText');
  const buttonUrl = isMyMicrosite ? videoQrData?.ctaUrl : watch('basicInfo.information.buttonURL');
  const videos = isMyMicrosite ? videoQrData?.videos : (watch('basicInfo.video.files') as Video[]);
  const backgroundColor = isMyMicrosite ? videoQrData?.design.primary : watch('appearance.design.primary');
  const titleColor = isMyMicrosite ? videoQrData?.fontsColors.title : watch('appearance.fonts.titleColor');
  const textColor = isMyMicrosite ? videoQrData?.fontsColors.text : watch('appearance.fonts.textsColor');
  const titleFont = isMyMicrosite ? videoQrData?.fonts.title : watch('appearance.fonts.title');
  const textFont = isMyMicrosite ? videoQrData?.fonts.text : watch('appearance.fonts.text');
  const showVideoDirectly = isMyMicrosite
    ? videoQrData?.highlightFirstVideo
    : watch('basicInfo.video.showVideoDirectly');

  if (videos.length === 1 && showVideoDirectly) {
    return (
      <Box
        sx={[{ backgroundColor }, styles.videoDirectlyWrapper]}
        className={isMyMicrosite ? 'myMicrosite' : undefined}
      >
        {videos[0].sourceType === 'YOUTUBE' ? (
          <iframe
            src={convertYouTubeToEmbed(videos[0].sourceUrl)}
            width="100%"
            height={isMyMicrosite ? undefined : '360'}
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            frameBorder="0"
          />
        ) : (
          <video src={videos[0].videoFile} poster={videos[0].thumbnail ?? undefined} controls />
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={[
        {
          ...mainPreviewContainerStyles,
          alignItems: 'normal',
          background: `linear-gradient(160deg, ${backgroundColor} 0%, ${backgroundColor} 50%, white 50%);`,
          padding: '20px 8px',
        },
        isMyMicrosite && { margin: 0, minHeight: '100vh', borderRadius: 0 },
        styles.wrapper,
      ]}
    >
      <Text
        sx={{
          color: textColor ?? COLORS.white,
          fontFamily: textFont,
          fontWeight: 500,
          textAlign: 'center',
          fontSize: 10,
          marginBottom: '5px',
        }}
      >
        {company}
      </Text>
      <Text
        sx={{
          color: titleColor ?? COLORS.white,
          fontFamily: titleFont,
          fontWeight: 700,
          textAlign: 'center',
          fontSize: 15,
          marginBottom: '5px',
        }}
      >
        {videoTitle}
      </Text>
      {!!description && (
        <Text
          sx={{
            color: textColor ?? COLORS.white,
            fontFamily: textFont,
            fontWeight: 500,
            textAlign: 'center',
            fontSize: 10,
            marginBottom: '5px',
          }}
        >
          {description}
        </Text>
      )}
      {buttonText && (
        <a href={buttonUrl || '#'} target="_blank" className="ctaButton">
          {buttonText}
        </a>
      )}
      {!!videos.length && (
        <div className="videosWrapper">
          {videos.map((video) => {
            return <VideoItem key={video.videoFile} video={video} />;
          })}
        </div>
      )}
    </Box>
  );
}

function VideoItem({ video }: { video: Video }) {
  return (
    <div className="videoItem">
      {video.sourceType === 'YOUTUBE' ? (
        <iframe
          src={convertYouTubeToEmbed(video.sourceUrl)}
          width="100%"
          title="YouTube video player"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          frameBorder="0"
        />
      ) : (
        <video src={video.videoFile} poster={video.thumbnail ?? undefined} controls />
      )}
      {!!video.description && <Text className="videoDescription">{video.description}</Text>}
    </div>
  );
}

const useStyles = () => {
  return {
    wrapper: css`
      box-sizing: border-box;
      padding: 20px 16px;

      .ctaButton {
        padding: 6px 14px;
        margin-top: 10px;
        border-radius: 5px;
        font-size: 10px;
        border: 1px solid white;
        color: white;
        text-decoration: none;
        text-align: center;
        cursor: pointer;
        &:hover {
          opacity: 0.9;
        }
      }

      .videosWrapper {
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin-top: 22px;
      }

      .videoItem {
        border-radius: 13px;
        padding: 10px;
        background: white;
        box-shadow: 0 2px 13px 0 ${COLORS.textMainOpacity15};
        
        video {
          width: 100%;
          max-height: 150px;
          border-radius: 13px;
        }
      }

      .videoDescription {
        font-size: 9px;
        text-align: center;
        color: ${COLORS.textMain}
        font-weight: 500;
      }
      
      .videoWrapper {
        position: relative;
      }
    `,
    videoDirectlyWrapper: css`
      cursor: initial;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: calc(100% - 29px);
      width: calc(100% - 27px);
      margin: 0 auto;
      border-radius: 36px;

      video {
        max-width: 100%;
      }

      &.myMicrosite {
        border-radius: 0;
        width: 100%;
        height: 100dvh;

        video {
          width: 100dvw;
          height: inherit;
        }

        & > iframe {
          height: calc(100dvh - 240px);
        }
      }
    `,
  };
};
